import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import {
    createFieldColumns,
    FieldColumnOptions,
} from "@/components/fields/fieldColumns";
import TableV2 from "@/components/table/TableV2";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { PaginationResponse } from "@/types/pagination";
import {
    CropCycleReportRow,
    FieldReportTableRow,
    toFieldReportTableRow,
} from "@/types/fieldReport";
import { getReportTypeLabel, ReportType } from "@/types/report";
import {
    useInfiniteQuery,
    useQueryClient,
    UseQueryResult,
} from "@tanstack/react-query";
import { ColumnDef, RowSelectionState, SortingState } from "@tanstack/react-table";
import {
    addToast,
    Button,
    Checkbox,
    Chip,
    Divider,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    NumberInput,
    Select,
    SelectItem,
} from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isNumber, roundTo } from "@/lib/utils/numbers";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) =>
    String(CURRENT_YEAR - i),
);

const REPORT_TYPE_OPTIONS = [
    { key: ReportType.PERFORMANCE_V2, label: "Performance" },
    { key: ReportType.TRIAL, label: "Trial" },
] as const;

const CURRENCY_FORMAT_OPTIONS = {
    style: "currency",
    currency: "USD",
} as const;

function formatMetric(value: number | null | undefined, decimals = 1): string {
    if (value == null || !isNumber(value)) return "--";
    return String(roundTo(value, decimals));
}

type SelectionConstraint =
    | { type: "csp"; id: number }
    | { type: "farm"; id: number }
    | { type: "invalid" };

function getSelectionConstraint(
    selectedRows: FieldReportTableRow[],
): SelectionConstraint | null {
    if (selectedRows.length === 0) return null;

    const serviceProviderIds = new Set(
        selectedRows.map((row) => row.service_provider).filter((id) => id > 0),
    );
    if (serviceProviderIds.size > 1) {
        return { type: "invalid" };
    }
    if (serviceProviderIds.size === 1) {
        return { type: "csp", id: [...serviceProviderIds][0] };
    }

    const growerIds = new Set(
        selectedRows.map((row) => row.grower).filter((id) => id > 0),
    );
    if (growerIds.size === 1) {
        return { type: "farm", id: [...growerIds][0] };
    }
    return { type: "invalid" };
}

function isRowSelectable(
    row: FieldReportTableRow,
    constraint: SelectionConstraint | null,
): boolean {
    if (!constraint) return true;
    if (constraint.type === "invalid") return false;
    if (constraint.type === "csp") {
        return row.service_provider === constraint.id;
    }
    return row.grower === constraint.id;
}

function getSelectableRows(
    tableData: FieldReportTableRow[],
    serviceProviderFilter: string | undefined,
    selectedRows: FieldReportTableRow[],
): FieldReportTableRow[] {
    if (serviceProviderFilter) {
        return tableData.filter(
            (row) => row.service_provider === Number(serviceProviderFilter),
        );
    }

    const cspIds = new Set(
        tableData.map((row) => row.service_provider).filter((id) => id > 0),
    );
    if (cspIds.size === 1) {
        return tableData;
    }

    const constraint = getSelectionConstraint(selectedRows);
    if (constraint?.type === "csp") {
        return tableData.filter(
            (row) => row.service_provider === constraint.id,
        );
    }
    if (constraint?.type === "farm") {
        return tableData.filter((row) => row.grower === constraint.id);
    }

    if (tableData.length === 0) {
        return tableData;
    }

    const firstServiceProviderId = tableData[0].service_provider;
    return tableData.filter(
        (row) => row.service_provider === firstServiceProviderId,
    );
}

type ReportSelectionSummary = {
    isValid: boolean;
    organizationId: number | null;
    defaultReportName: string;
    invalidReason: string | null;
};

function summarizeReportSelection(
    selectedRows: FieldReportTableRow[],
    growersById: Record<number, Organization>,
    serviceProvidersById: Record<number, Organization>,
    reportType: ReportType,
): ReportSelectionSummary {
    const reportTypeLabel = getReportTypeLabel(reportType);

    if (selectedRows.length === 0) {
        return {
            isValid: false,
            organizationId: null,
            defaultReportName: "",
            invalidReason: null,
        };
    }

    const constraint = getSelectionConstraint(selectedRows);
    if (!constraint || constraint.type === "invalid") {
        return {
            isValid: false,
            organizationId: null,
            defaultReportName: "",
            invalidReason:
                "Selected fields must belong to the same CSP or farm.",
        };
    }

    const growerIds = new Set(
        selectedRows.map((row) => row.grower).filter((id) => id > 0),
    );

    if (constraint.type === "farm") {
        const growerName =
            growersById[constraint.id]?.name ?? reportTypeLabel;
        return {
            isValid: true,
            organizationId: constraint.id,
            defaultReportName: `${growerName} ${reportTypeLabel}`,
            invalidReason: null,
        };
    }

    if (growerIds.size === 1) {
        const growerName =
            growersById[[...growerIds][0]]?.name ?? reportTypeLabel;
        return {
            isValid: true,
            organizationId: [...growerIds][0],
            defaultReportName: `${growerName} ${reportTypeLabel}`,
            invalidReason: null,
        };
    }

    const cspName =
        serviceProvidersById[constraint.id]?.name ?? reportTypeLabel;
    return {
        isValid: true,
        organizationId: constraint.id,
        defaultReportName: `${cspName} ${reportTypeLabel}`,
        invalidReason: null,
    };
}

export default function DocumentationReports({
    onCreated,
    serviceProvidersQuery,
    growersQuery,
    accountManagersQuery,
}: {
    onCreated?: () => void;
    serviceProvidersQuery: UseQueryResult<Organization[], Error>;
    growersQuery: UseQueryResult<Organization[], Error>;
    accountManagersQuery: UseQueryResult<User[], Error>;
}) {
    const { axiosRequest, useMutateRequest } = useRequest();
    const { userIsSentinel } = useAuth();
    const queryClient = useQueryClient();

    const [search, setSearch] = useState("");
    const [serviceProvider, setServiceProvider] = useState<
        string | undefined
    >();
    const [grower, setGrower] = useState<string | undefined>();
    const [accountManager, setAccountManager] = useState<string | undefined>();
    const [enrollmentTier, setEnrollmentTier] = useState<string | undefined>();
    const [selectedYear, setSelectedYear] = useState(String(CURRENT_YEAR));
    const [reportType, setReportType] = useState<ReportType>(
        ReportType.PERFORMANCE_V2,
    );
    const [sorting, setSorting] = useState<SortingState>([
        { id: "name", desc: false },
    ]);

    const [selectedRows, setSelectedRows] = useState<FieldReportTableRow[]>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [reportName, setReportName] = useState("");
    const [cropPrice, setCropPrice] = useState<number | undefined>(4);
    const [inputCost, setInputCost] = useState<number | undefined>(0.9);
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [bulkTypicalN, setBulkTypicalN] = useState<number | undefined>();
    const [bulkPlannedN, setBulkPlannedN] = useState<number | undefined>();
    const [bulkTypicalYield, setBulkTypicalYield] = useState<
        number | undefined
    >();
    const [bulkYieldGoal, setBulkYieldGoal] = useState<number | undefined>();
    const [bulkEditSaving, setBulkEditSaving] = useState(false);

    const growersById = useMemo(() => {
        return (
            growersQuery.data?.reduce(
                (acc, grower) => {
                    acc[grower.id] = grower;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [growersQuery.data]);

    const serviceProvidersById = useMemo(() => {
        return (
            serviceProvidersQuery.data?.reduce(
                (acc, serviceProvider) => {
                    acc[serviceProvider.id] = serviceProvider;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [serviceProvidersQuery.data]);

    const accountManagersById = useMemo(() => {
        return (
            accountManagersQuery.data?.reduce(
                (acc, accountManager) => {
                    acc[accountManager.id] = accountManager;
                    return acc;
                },
                {} as Record<number, User>,
            ) ?? {}
        );
    }, [accountManagersQuery.data]);

    const growerOptions = useMemo(() => {
        return (
            growersQuery.data?.map((grower) => ({
                label: grower.name,
                key: grower.id.toString(),
            })) ?? []
        );
    }, [growersQuery.data]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((serviceProvider) => ({
                label: serviceProvider.name,
                key: serviceProvider.id.toString(),
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const accountManagerOptions = useMemo(() => {
        return (
            accountManagersQuery.data?.map((accountManager) => ({
                label: accountManager.full_name,
                key: accountManager.id.toString(),
            })) ?? []
        );
    }, [accountManagersQuery.data]);

    const apiOrdering = useMemo(() => {
        if (sorting.length === 0) return "name";
        const id = sorting[0].id.startsWith("-")
            ? sorting[0].id.slice(1)
            : sorting[0].id;
        if (id !== "grower" && id !== "name") return "name";
        return `${sorting[0].desc ? "-" : ""}${id}`;
    }, [sorting]);

    const fieldsQuery = useInfiniteQuery<PaginationResponse<CropCycleReportRow>>({
        queryKey: [
            "crop_cycles",
            "reports",
            search,
            apiOrdering,
            serviceProvider,
            grower,
            accountManager,
            enrollmentTier,
            selectedYear,
            reportType,
        ],
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<CropCycleReportRow>>({
                url: "crop_cycles/reports/",
                params: {
                    search,
                    ordering: apiOrdering,
                    service_provider: serviceProvider,
                    grower,
                    account_manager: accountManager,
                    enrollment_tier: enrollmentTier,
                    year: selectedYear,
                    enrollment_type:
                        reportType === ReportType.TRIAL
                            ? ReportType.TRIAL
                            : undefined,
                    page: pageParam,
                    size: 40,
                },
            });
        },
    });

    const createReport = useMutateRequest({
        url: "reports/",
        method: "post",
        successMessage: "Report generation started",
    });

    const updateFieldsBulk = useMutateRequest({
        url: "fields/bulk_update/",
        method: "patch",
    });

    const upsertCropCyclesBulk = useMutateRequest({
        url: "crop_cycles/bulk_upsert/",
        method: "post",
    });

    const openBulkEdit = useCallback(() => {
        if (selectedRows.length === 1) {
            const row = selectedRows[0];
            setBulkTypicalN(
                row.typical_N_applied_pounds_per_acre ?? undefined,
            );
            setBulkPlannedN(
                row.cropCycle?.planned_total_nitrogen ?? undefined,
            );
            setBulkTypicalYield(
                row.typical_yield_bushels_per_acre ?? undefined,
            );
            setBulkYieldGoal(row.cropCycle?.yield_goal ?? undefined);
        } else {
            setBulkTypicalN(undefined);
            setBulkPlannedN(undefined);
            setBulkTypicalYield(undefined);
            setBulkYieldGoal(undefined);
        }
        setBulkEditOpen(true);
    }, [selectedRows]);

    const handleBulkEditSave = useCallback(async () => {
        const fieldData: Record<string, number> = {};
        if (bulkTypicalN != null) {
            fieldData.typical_N_applied_pounds_per_acre = bulkTypicalN;
        }
        if (bulkTypicalYield != null) {
            fieldData.typical_yield_bushels_per_acre = bulkTypicalYield;
        }

        const cropCycleData: Record<string, number> = {};
        if (bulkPlannedN != null) {
            cropCycleData.planned_total_nitrogen = bulkPlannedN;
        }
        if (bulkYieldGoal != null) {
            cropCycleData.yield_goal = bulkYieldGoal;
        }

        if (
            Object.keys(fieldData).length === 0 &&
            Object.keys(cropCycleData).length === 0
        ) {
            addToast({
                title: "Enter at least one value to update",
                timeout: 3000,
                color: "danger",
            });
            return;
        }

        const mutations: Promise<unknown>[] = [];

        if (Object.keys(fieldData).length > 0) {
            const fieldIds = [
                ...new Set(selectedRows.map((row) => row.id)),
            ];
            mutations.push(
                updateFieldsBulk.mutateAsync({
                    url: "fields/bulk_update/",
                    method: "patch",
                    data: fieldIds.map((id) => ({ id, ...fieldData })),
                }),
            );
        }

        if (Object.keys(cropCycleData).length > 0) {
            const cropCyclePayload = selectedRows
                .filter((row) => row.cropCycle?.id != null)
                .map((row) => ({
                    id: row.cropCycle!.id,
                    field: row.id,
                    ...cropCycleData,
                }));
            if (cropCyclePayload.length > 0) {
                mutations.push(
                    upsertCropCyclesBulk.mutateAsync({
                        url: "crop_cycles/bulk_upsert/",
                        method: "post",
                        data: cropCyclePayload,
                    }),
                );
            }
        }

        setBulkEditSaving(true);
        try {
            await Promise.all(mutations);
            addToast({
                title: "Values updated successfully",
                timeout: 3000,
                color: "success",
            });
            setBulkEditOpen(false);
            await queryClient.invalidateQueries({
                queryKey: ["crop_cycles", "reports"],
            });
        } catch {
            addToast({
                title: "Failed to update values",
                timeout: 3000,
                color: "danger",
            });
        } finally {
            setBulkEditSaving(false);
        }
    }, [
        bulkTypicalN,
        bulkTypicalYield,
        bulkPlannedN,
        bulkYieldGoal,
        selectedRows,
        updateFieldsBulk,
        upsertCropCyclesBulk,
        queryClient,
    ]);

    const tableData = useMemo(() => {
        const rows =
            fieldsQuery.data?.pages.flatMap((page) => page.results) ?? [];
        return rows.map((row) =>
            toFieldReportTableRow(row, {
                growersById,
                serviceProvidersById,
                accountManagersById,
            }),
        );
    }, [
        fieldsQuery.data?.pages,
        growersById,
        serviceProvidersById,
        accountManagersById,
    ]);

    const selectionConstraint = useMemo(
        () => getSelectionConstraint(selectedRows),
        [selectedRows],
    );

    const selectionSummary = useMemo(
        () =>
            summarizeReportSelection(
                selectedRows,
                growersById,
                serviceProvidersById,
                reportType,
            ),
        [selectedRows, growersById, serviceProvidersById, reportType],
    );

    const selectableRows = useMemo(
        () => getSelectableRows(tableData, serviceProvider, selectedRows),
        [tableData, serviceProvider, selectedRows],
    );

    const handleSelectAll = useCallback(
        (select: boolean) => {
            if (!select) {
                setRowSelection({});
                return;
            }

            setRowSelection(
                Object.fromEntries(
                    selectableRows.map((row) => [row.rowId, true]),
                ),
            );
        },
        [selectableRows],
    );

    useEffect(() => {
        setRowSelection({});
    }, [
        search,
        serviceProvider,
        grower,
        accountManager,
        enrollmentTier,
        selectedYear,
        reportType,
    ]);

    useEffect(() => {
        if (selectedRows.length === 0) {
            setReportName("");
            return;
        }
        setReportName(selectionSummary.defaultReportName);
    }, [selectedRows, selectionSummary.defaultReportName]);

    const handleGenerate = useCallback(() => {
        if (selectedRows.length === 0 || !selectionSummary.isValid) return;
        if (cropPrice == null || inputCost == null) return;
        if (selectionSummary.organizationId == null) return;

        const cropCycleConfigs = selectedRows
            .filter((row) => row.cropCycle?.id != null)
            .map((row) => ({
                crop_cycle: row.cropCycle!.id,
                crop_price: cropPrice,
                input_cost: inputCost,
            }));

        if (cropCycleConfigs.length === 0) return;

        createReport.mutate(
            {
                data: {
                    report_type: reportType,
                    year: Number(selectedYear),
                    organization: selectionSummary.organizationId,
                    crop_price: cropPrice,
                    input_cost: inputCost,
                    name: reportName || selectionSummary.defaultReportName,
                    crop_cycle_configs: cropCycleConfigs,
                },
            },
            {
                onSuccess: () => {
                    onCreated?.();
                },
            },
        );
    }, [
        selectedRows,
        selectionSummary,
        cropPrice,
        inputCost,
        selectedYear,
        reportName,
        reportType,
        createReport,
        onCreated,
    ]);

    const canGenerate = useMemo(() => {
        return (
            selectionSummary.isValid &&
            cropPrice != null &&
            inputCost != null &&
            selectedRows.every((row) => row.cropCycle?.id != null)
        );
    }, [selectedRows, selectionSummary, cropPrice, inputCost]);

    const allSelectableRowsSelected = useMemo(() => {
        if (selectableRows.length === 0) return false;
        return selectableRows.every((row) => rowSelection[row.rowId]);
    }, [selectableRows, rowSelection]);

    const someSelectableRowsSelected = useMemo(() => {
        return selectableRows.some((row) => rowSelection[row.rowId]);
    }, [selectableRows, rowSelection]);

    const columns: ColumnDef<FieldReportTableRow>[] = useMemo(() => {
        const columnOptions: FieldColumnOptions = {
            growersById,
            serviceProvidersById,
            accountManagersById,
            userIsSentinel,
        };

        return [
            {
                id: "selection",
                header: () => {
                    return (
                        <Checkbox
                            className="pl-4"
                            isSelected={allSelectableRowsSelected}
                            isIndeterminate={
                                someSelectableRowsSelected &&
                                !allSelectableRowsSelected
                            }
                            onValueChange={handleSelectAll}
                        />
                    );
                },
                enableSorting: false,
                accessorFn: (row) => row?.id ?? "",
                cell: ({ row }) => {
                    const rowSelectable = isRowSelectable(
                        row.original,
                        selectionConstraint,
                    );
                    return (
                        <Checkbox
                            className="pl-4"
                            isSelected={row.getIsSelected()}
                            isDisabled={!rowSelectable}
                            onValueChange={(value) => row.toggleSelected(value)}
                        />
                    );
                },
                size: 50,
            } as ColumnDef<FieldReportTableRow>,
            createFieldColumns.growerAndServiceProvider(
                columnOptions,
            ) as ColumnDef<FieldReportTableRow>,
            createFieldColumns.fieldName() as ColumnDef<FieldReportTableRow>,
            {
                id: "crop",
                header: "Crop",
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.planting_date ?? null,
                cell: ({ row }) => {
                    const cropCycle = row.original.cropCycle;
                    if (!cropCycle) return "--";

                    const source =
                        cropCycle.planting_operation != null &&
                            cropCycle.planting_operation !== -1
                            ? "Logged"
                            : cropCycle.planting_date
                                ? "Default"
                                : null;

                    return (
                        <div className="flex flex-col items-start w-full text-sm pr-2">
                            <div className="font-bold">
                                {cropCycle.crop ?? "--"}
                            </div>
                            <div className="flex flex-row items-center justify-between w-full">
                                <span className="text-xs text-gray-500">
                                    {cropCycle.planting_date
                                        ? dayjs(cropCycle.planting_date).format(
                                            "ll",
                                        )
                                        : "--"}
                                </span>
                                {source && (
                                    <Chip
                                        color={
                                            source === "Logged"
                                                ? "secondary"
                                                : "default"
                                        }
                                        size="sm"
                                        classNames={{ base: "text-xs" }}
                                        radius="sm"
                                    >
                                        {source}
                                    </Chip>
                                )}
                            </div>
                        </div>
                    );
                },
                size: 180,
            },
            {
                id: "typical_N_applied_pounds_per_acre",
                header: () => (
                    <span className="w-full text-right">Typical N</span>
                ),
                enableSorting: false,
                accessorFn: (row) =>
                    row.typical_N_applied_pounds_per_acre ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(
                            row.original.typical_N_applied_pounds_per_acre,
                            0,
                        )}
                    </span>
                ),
                size: 90,
            },
            {
                id: "planned_total_nitrogen",
                header: () => (
                    <span className="w-full text-right">Planned N</span>
                ),
                enableSorting: false,
                accessorFn: (row) =>
                    row.cropCycle?.planned_total_nitrogen ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(
                            row.original.cropCycle?.planned_total_nitrogen,
                            0,
                        )}
                    </span>
                ),
                size: 90,
            },
            {
                id: "n_applied",
                header: () => (
                    <span className="w-full text-right">N Applied</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.n_applied ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(row.original.cropCycle?.n_applied, 0)}
                    </span>
                ),
                size: 90,
            },
            {
                id: "typical_yield_bushels_per_acre",
                header: () => (
                    <span className="w-full text-right">Typical Yield</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.typical_yield_bushels_per_acre ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(
                            row.original.typical_yield_bushels_per_acre,
                            0,
                        )}
                    </span>
                ),
                size: 110,
            },
            {
                id: "yield_goal",
                header: () => (
                    <span className="w-full text-right">Yield Goal</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.yield_goal ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(row.original.cropCycle?.yield_goal, 0)}
                    </span>
                ),
                size: 90,
            },
            {
                id: "harvest",
                header: () => (
                    <span className="w-full text-right">Harvest</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.average_yield ?? null,
                cell: ({ row }) => {
                    const cropCycle = row.original.cropCycle;
                    const harvestDate = cropCycle?.harvest_date;
                    const yieldValue = cropCycle?.average_yield;
                    if (harvestDate == null && yieldValue == null) {
                        return (
                            <span className="w-full text-right">--</span>
                        );
                    }
                    return (
                        <div className="flex flex-col items-end w-full text-sm">
                            <div className="font-bold">
                                {yieldValue != null
                                    ? formatMetric(yieldValue, 0)
                                    : "--"}
                            </div>
                            <span className="text-xs text-gray-500">
                                {harvestDate
                                    ? dayjs(harvestDate).format("ll")
                                    : "--"}
                            </span>
                        </div>
                    );
                },
                size: 120,
            },
            {
                id: "nue",
                header: () => <span className="w-full text-right">NUE</span>,
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.nue ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(row.original.cropCycle?.nue, 2)}
                    </span>
                ),
                size: 80,
            },
            {
                id: "si_min",
                header: () => (
                    <span className="w-full text-right">SI Min</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.si_min ?? null,
                cell: ({ row }) => {
                    const value = row.original.cropCycle?.si_min;
                    return (
                        <span className="w-full text-right">
                            {value == null || !isNumber(value)
                                ? "--"
                                : `${roundTo(value * 100, 1)}%`}
                        </span>
                    );
                },
                size: 80,
            },
            {
                id: "si_mean",
                header: () => (
                    <span className="w-full text-right">SI Mean</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.si_mean ?? null,
                cell: ({ row }) => {
                    const value = row.original.cropCycle?.si_mean;
                    return (
                        <span className="w-full text-right">
                            {value == null || !isNumber(value)
                                ? "--"
                                : `${roundTo(value * 100, 1)}%`}
                        </span>
                    );
                },
                size: 80,
            },
            {
                id: "total_irrigation",
                header: () => (
                    <span className="w-full text-right">Irrigation</span>
                ),
                enableSorting: false,
                accessorFn: (row) => row.cropCycle?.total_irrigation ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {formatMetric(
                            row.original.cropCycle?.total_irrigation,
                            1,
                        )}
                    </span>
                ),
                size: 90,
            },
            {
                id: "average_h2o_sufficiency",
                header: () => (
                    <span className="w-full text-right">H2O Sufficiency</span>
                ),
                enableSorting: false,
                accessorFn: (row) =>
                    row.cropCycle?.average_h2o_sufficiency ?? null,
                cell: ({ row }) => (
                    <span className="w-full text-right">
                        {row.original.cropCycle?.average_h2o_sufficiency !=
                            null &&
                            isNumber(
                                row.original.cropCycle?.average_h2o_sufficiency,
                            )
                            ? `${formatMetric(row.original.cropCycle?.average_h2o_sufficiency, 1)}%`
                            : "--"}
                    </span>
                ),
                size: 120,
            },
        ];
    }, [
        growersById,
        serviceProvidersById,
        accountManagersById,
        userIsSentinel,
        selectionConstraint,
        allSelectableRowsSelected,
        someSelectableRowsSelected,
        handleSelectAll,
    ]);

    const isLoading = useMemo(() => {
        return (
            fieldsQuery.isLoading ||
            serviceProvidersQuery.isLoading ||
            growersQuery.isLoading ||
            accountManagersQuery.isLoading
        );
    }, [
        fieldsQuery.isLoading,
        serviceProvidersQuery.isLoading,
        growersQuery.isLoading,
        accountManagersQuery.isLoading,
    ]);

    return (
        <div className="h-full flex flex-col overflow-hidden bg-white">
            <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                <div className="flex flex-col lg:flex-row gap-2 items-center">
                    <FieldFilterBar
                        searchValue={search}
                        onSearchChange={setSearch}
                        serviceProviderId={serviceProvider}
                        onServiceProviderChange={setServiceProvider}
                        serviceProviderOptions={serviceProviderOptions}
                        growerId={grower}
                        onGrowerChange={setGrower}
                        growerOptions={growerOptions}
                        accountManagerId={accountManager}
                        onAccountManagerChange={setAccountManager}
                        accountManagerOptions={accountManagerOptions}
                        enrollmentTier={enrollmentTier}
                        onEnrollmentTierChange={setEnrollmentTier}
                    />
                    <Select
                        selectionMode="single"
                        size="sm"
                        aria-label="Year"
                        className="w-28"
                        selectedKeys={new Set([selectedYear])}
                        onSelectionChange={(keys) => {
                            const value = Array.from(keys)[0];
                            if (value == null) return;
                            setSelectedYear(String(value));
                        }}
                    >
                        {YEAR_OPTIONS.map((y) => (
                            <SelectItem key={y}>{y}</SelectItem>
                        ))}
                    </Select>
                </div>
                <Button
                    size="sm"
                    color="primary"
                    startContent={<FontAwesomeIcon icon={faPen} />}
                    isDisabled={selectedRows.length === 0}
                    onPress={openBulkEdit}
                >
                    Edit N & Yield Values
                    {selectedRows.length > 0
                        ? ` (${selectedRows.length})`
                        : ""}
                </Button>
            </div>
            <Divider />
            <TableV2
                data={tableData}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                manualSorting
                isLoading={isLoading}
                hasNextPage={fieldsQuery.hasNextPage}
                fetchNextPage={fieldsQuery.fetchNextPage}
                isFetchingNextPage={fieldsQuery.isFetchingNextPage}
                pinnedColumns={["selection", "grower", "name"]}
                showEndMessage
                showScrollbar={true}
                getRowId={(row) => row.rowId}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                onSelectionChange={setSelectedRows}
            />
            <Divider />
            <div className="flex flex-col lg:flex-row gap-2 p-2 items-end">
                <Select
                    selectionMode="single"
                    size="sm"
                    label="Report Type"
                    className="lg:w-44"
                    selectedKeys={new Set([reportType])}
                    onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0];
                        if (value == null) return;
                        setReportType(value as ReportType);
                    }}
                >
                    {REPORT_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.key}>{option.label}</SelectItem>
                    ))}
                </Select>
                <Input
                    size="sm"
                    label="Report Name"
                    className="lg:w-64"
                    value={reportName}
                    onValueChange={setReportName}
                />
                <NumberInput
                    size="sm"
                    label="Crop Price"
                    className="lg:w-36"
                    value={cropPrice}
                    onValueChange={setCropPrice}
                    min={0}
                    formatOptions={CURRENCY_FORMAT_OPTIONS}
                />
                <NumberInput
                    size="sm"
                    label="Input Cost"
                    className="lg:w-36"
                    value={inputCost}
                    onValueChange={setInputCost}
                    min={0}
                    formatOptions={CURRENCY_FORMAT_OPTIONS}
                />
                <Button
                    size="lg"
                    color="primary"
                    className="min-w-40 font-semibold"
                    isDisabled={!canGenerate}
                    isLoading={createReport.isPending}
                    onPress={handleGenerate}
                >
                    Generate Report
                </Button>
            </div>
            {selectionSummary.invalidReason && (
                <p className="px-2 pb-2 text-sm text-danger">
                    {selectionSummary.invalidReason}
                </p>
            )}
            <Modal
                isOpen={bulkEditOpen}
                onClose={() => setBulkEditOpen(false)}
                size="lg"
            >
                <ModalContent>
                    <ModalHeader>
                        Edit N & Yield Values
                        {selectedRows.length > 0
                            ? ` (${selectedRows.length})`
                            : ""}
                    </ModalHeader>
                    <ModalBody className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <NumberInput
                            size="sm"
                            label="Typical N"
                            value={bulkTypicalN}
                            onValueChange={setBulkTypicalN}
                            min={0}
                        />
                        <NumberInput
                            size="sm"
                            label="Planned N"
                            value={bulkPlannedN}
                            onValueChange={setBulkPlannedN}
                            min={0}
                        />
                        <NumberInput
                            size="sm"
                            label="Typical Yield"
                            value={bulkTypicalYield}
                            onValueChange={setBulkTypicalYield}
                            min={0}
                        />
                        <NumberInput
                            size="sm"
                            label="Yield Goal"
                            value={bulkYieldGoal}
                            onValueChange={setBulkYieldGoal}
                            min={0}
                        />
                        {selectedRows.length > 1 && (
                            <p className="sm:col-span-2 text-sm text-default-500">
                                Only filled values will be applied to selected
                                rows.
                            </p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setBulkEditOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            isLoading={bulkEditSaving}
                            onPress={() => {
                                void handleBulkEditSave();
                            }}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}
